import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {ProvisionRunDto} from '../../../model/dto/entity/provision-run.dto';
import {CpRunActivationSummary} from '../../../interface/cp-run-activation-summary.interface';
import {CpRunActivationSummaryService} from '../../../service/domain/cp-run-activation-summary.service';

/** Load state of the summary — the dialog renders one branch per value. */
type CpRunActivationSummaryState = 'idle' | 'loading' | 'loaded';

/**
 * Confirmation dialog in front of "Aktivieren" on the step bar of a provision run.
 *
 * Replaces the one-line ConfirmationService prompt: before the calculated bookings become money the
 * operator sees the provision sum, the number of bookings that will be activated, the contracts in
 * ERROR, the open M-026 warnings and when the run was last calculated
 * ({@link CpRunActivationSummaryService}). The dialog itself changes nothing — it emits
 * {@link accepted} and the step bar fires `POST /{id}/activate` through its usual busy/polling path.
 *
 * ── WHAT THE TEXT SAYS ABOUT ERROR CONTRACTS, AND WHY EXACTLY THAT
 *
 * Only what the backend guarantees is stated:
 *   * the activation flips the run's booking pairs that are still CALCULATED and nothing else
 *     (`ProvisionActivationService` — `SQL_CALCULATED_PAIRS` and the two `JPQL_ACTIVATE_*` updates
 *     carry `status = CALCULATED`);
 *   * a contract with outcome ERROR was not reconciled (P-03, `ProvisionCalculationService
 *     .writeResult`: `if (calc.errored) … no reconcile`), so this run wrote no booking for it and
 *     the activation books nothing for it; its already ACTIVE bookings are never touched (U-05);
 *   * after the activation the run cannot be recalculated (`ProvisionRunService.START_CALCULATION`
 *     = NEW, CALCULATED, IN_REVIEW, CALCULATION_FAILED);
 *   * whether a LATER run recalculates the contract depends on the contract selection
 *     (`ProvisionContractSelectionService`, four OR-ed branches) — it is likely for a contract that
 *     started on/after go-live, but not guaranteed, and the dialog says so instead of promising it.
 *
 * ── EXPLICIT CONFIRMATION
 *
 * With open errors — or when the error figures could not be read at all — the accept button stays
 * disabled until the operator ticks the acknowledgement. An unknown count is treated like an
 * error: "could not check" must not read as "nothing to check".
 */
@Component({
    selector: 'cp-run-activation-confirm-dialog',
    templateUrl: './cp-run-activation-confirm-dialog.component.html',
    styleUrls: ['./cp-run-activation-confirm-dialog.component.scss'],
    standalone: false
})
export class CpRunActivationConfirmDialogComponent implements OnInit, OnChanges, OnDestroy {

    busy: boolean;
    initialized: boolean;

    @Input() visible: boolean = false;
    @Input() provisionRunDto: ProvisionRunDto;

    @Output() visibleChange = new EventEmitter<boolean>();

    /** The operator confirmed — the host starts the activation. */
    @Output() accepted = new EventEmitter<void>();

    summaryState: CpRunActivationSummaryState = 'idle';
    summary: CpRunActivationSummary;

    /** The explicit acknowledgement that is required while errors are open (or unknown). */
    errorsAcknowledged: boolean = false;

    private summarySubscription: Subscription;

    constructor(protected activationSummaryService: CpRunActivationSummaryService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    /**
     * Initialize component.
     */
    initComponent(): void {
        this.initialized = true;
    }

    /**
     * Refresh component — reload the summary for the current run. Called every time the dialog is
     * shown, because the figures change with every recalculation.
     */
    refreshComponent(): void {

        this.summarySubscription?.unsubscribe();
        this.errorsAcknowledged = false;
        this.summary = null;

        if (!this.provisionRunDto?.id) {
            this.summaryState = 'idle';
            return;
        }

        this.summaryState = 'loading';
        this.busy = true;

        this.summarySubscription = this.activationSummaryService
            .getActivationSummary(this.provisionRunDto.id)
            .subscribe(summary => {
                this.summary = summary;
                this.summaryState = 'loaded';
                this.busy = false;
            });
    }

    handleShow(): void {
        this.refreshComponent();
    }

    handleVisibleChange(visible: boolean): void {
        this.visible = visible;
        this.visibleChange.emit(visible);
    }

    handleCancel(): void {
        this.handleVisibleChange(false);
    }

    handleAccept(): void {

        if (!this.canAccept()) {
            return;
        }

        this.handleVisibleChange(false);
        this.accepted.emit();
    }

    // ── Derived state ───────────────────────────────────────────────────────────────────────────

    /** Contracts in ERROR or open ERROR messages on contract or run level. */
    hasOpenErrors(): boolean {
        return (this.summary?.errorContractCount ?? 0) > 0
            || (this.summary?.openErrorMessageCount ?? 0) > 0
            || (this.summary?.openRunErrorMessageCount ?? 0) > 0;
    }

    /** At least one error figure could not be read — treated like an open error. */
    hasUnknownErrorFigures(): boolean {
        return this.summary?.errorContractCount == null
            || this.summary?.openErrorMessageCount == null
            || this.summary?.openRunErrorMessageCount == null;
    }

    requiresAcknowledgement(): boolean {
        return this.hasOpenErrors() || this.hasUnknownErrorFigures();
    }

    canAccept(): boolean {
        return this.summaryState === 'loaded'
            && (!this.requiresAcknowledgement() || this.errorsAcknowledged);
    }

    /** Nothing CALCULATED in the run — the activation would only move the status. */
    hasNothingToActivate(): boolean {
        return this.summary?.calculatedBookingCount === 0;
    }

    // ── Formatting ──────────────────────────────────────────────────────────────────────────────

    formatCurrency(value: number | null | undefined): string {
        if (value == null) {
            return '–';
        }
        return new Intl.NumberFormat('de-DE', {style: 'currency', currency: 'EUR'}).format(value);
    }

    formatCount(value: number | null | undefined): string {
        if (value == null) {
            return '–';
        }
        return new Intl.NumberFormat('de-DE').format(value);
    }

    /**
     * Process changes within binding.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (!this.initialized) {
            return;
        }

        // a different run while the dialog is open — the shown figures belong to the old one
        if (changes['provisionRunDto'] && this.visible
            && changes['provisionRunDto'].previousValue?.id !== this.provisionRunDto?.id) {
            this.refreshComponent();
        }
    }

    /**
     * Destroy component.
     */
    ngOnDestroy(): void {
        this.summarySubscription?.unsubscribe();
    }
}
